"use client";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { forwardRef, useState } from "react";
import { cn } from "../../utils/cn.js";
import { Button } from "../Button/Button.js";
import { CheckboxWithLabel } from "../Checkbox/CheckboxWithLabel/CheckboxWithLabel.js";
import { Image } from "../Image/Image.js";
import { InputText } from "../InputText/InputText.js";
import { ResponsiveWrapper, useResponsive } from "../ResponsiveWrapper/index.js";
import { StatusBanner } from "../StatusBanner/StatusBanner.js";
import { Span } from "../Text/Span.js";
import { acquisitionFormVariants } from "./AcquisitionForm.variants.js";
const EMAIL_REGEXP = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/;
const DEFAULT_QR_CODE_URL = 'https://images.ctfassets.net/sdx4pteldsvw/6Pj2oxVul6riwqJYTo4WgA/20b3526864ad4d632216df978ce44e22/House_Ads_QR_Code_TXT2_Download_LP_Smartlink-BLK__1_.svg';
const PHONE_FORMAT = {
    0: '+',
    1: '1',
    2: ' ',
    3: '(',
    7: ')',
    8: ' ',
    12: '-'
};
const MAX_PHONE_LENGTH = 17;
function isValidPhoneFormat(value) {
    for(let i = 0; i < value.length; i++){
        const char = value[i];
        const expectedChar = PHONE_FORMAT[i];
        if (expectedChar && expectedChar !== char) return false;
        if (!expectedChar && Number.isNaN(Number.parseInt(char))) return false;
    }
    return true;
}
function isValidPhoneNumber(value) {
    return isValidPhoneFormat(value) && value.length === MAX_PHONE_LENGTH;
}
function isValidEmail(value) {
    return EMAIL_REGEXP.test(value);
}
function applyPhoneLengthFormat(value, isDeleting) {
    if (7 === value.length && !isDeleting) return `${value}) `;
    if (8 === value.length && isDeleting) return value.substring(0, 6);
    if (12 === value.length && !isDeleting) return `${value}-`;
    if (13 === value.length && isDeleting) return value.substring(0, 11);
    return null;
}
function QrCodeBlock({ qrCodeUrl, qrCodeAlt, qrCodeText, qrCodeWrapperClass, qrCodeImageClass, qrCodeTextClass }) {
    return /*#__PURE__*/ jsxs("div", {
        className: qrCodeWrapperClass,
        children: [
            /*#__PURE__*/ jsx(Image, {
                src: qrCodeUrl,
                alt: qrCodeAlt,
                isSvg: true,
                className: qrCodeImageClass
            }),
            /*#__PURE__*/ jsx(Span, {
                className: qrCodeTextClass,
                children: qrCodeText
            })
        ]
    });
}
function AcquisitionFormContent({ type, onSubmit, showNameFields: showNameFieldsProp, isNameRequired: isNameRequiredProp, termsText, disclaimerText, childrenSlot, buttonText = 'Sign Up', emailPlaceholder = 'Enter your email', phonePlaceholder = '+1 (###) ###-####', firstNamePlaceholder = 'First name', lastNamePlaceholder = 'Last name', isLoading = false, successMessage, errorMessage, emphasis = 'regular', layout = 'inline', qrCodeUrl, qrCodeAlt = 'Scan to download app', qrCodeText = 'Scan our QR code', qrCodePosition = 'left', className }) {
    const { md } = useResponsive();
    const isSweepstakes = 'sweepstakes' === type;
    const isAppDownload = 'appDownload' === type;
    const showNameFields = isSweepstakes || showNameFieldsProp;
    const isNameRequired = isSweepstakes || isNameRequiredProp;
    const showEmailInput = 'email' === type || 'sweepstakes' === type;
    const showPhoneInput = 'sms' === type || isAppDownload;
    const resolvedQrCodeUrl = qrCodeUrl ?? DEFAULT_QR_CODE_URL;
    const useAppDownloadLayout = isAppDownload;
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [firstNameError, setFirstNameError] = useState('');
    const [lastNameError, setLastNameError] = useState('');
    const [termsError, setTermsError] = useState(false);
    const styles = acquisitionFormVariants({
        emphasis,
        layout,
        appDownloadColumn: useAppDownloadLayout
    });
    function formatShortPhoneInput(value, isDeleting) {
        if (value.length >= 4) return null;
        if (isDeleting) return '';
        if (/\D/.test(value)) return phoneNumber;
        if (3 === value.length) return `+1 (${value}) `;
        return `+1 (${value}`;
    }
    function formatPhoneNumber(value) {
        const isDeleting = value.length < phoneNumber.length;
        const shortResult = formatShortPhoneInput(value, isDeleting);
        if (null !== shortResult) return shortResult;
        if (!isValidPhoneFormat(value)) return phoneNumber;
        const lengthResult = applyPhoneLengthFormat(value, isDeleting);
        if (null !== lengthResult) return lengthResult;
        if (value.length > MAX_PHONE_LENGTH && !isDeleting) return phoneNumber;
        return value;
    }
    function handlePhoneChange(e) {
        const formatted = formatPhoneNumber(e.target.value);
        setPhoneNumber(formatted);
        if (phoneError) setPhoneError('');
    }
    function handleEmailChange(e) {
        setEmail(e.target.value);
        if (emailError) setEmailError('');
    }
    function handleFirstNameChange(e) {
        setFirstName(e.target.value);
        if (firstNameError) setFirstNameError('');
    }
    function handleLastNameChange(e) {
        setLastName(e.target.value);
        if (lastNameError) setLastNameError('');
    }
    function handleTermsChange() {
        setAgreedToTerms(!agreedToTerms);
        if (termsError && !agreedToTerms) setTermsError(false);
    }
    function validateEmail() {
        if (!showEmailInput) return true;
        if (!email) {
            setEmailError('Please enter an email address');
            return false;
        }
        if (!isValidEmail(email)) {
            setEmailError('Please check the email address you have entered');
            return false;
        }
        return true;
    }
    function validateForm() {
        let isValid = true;
        if (!validateEmail()) isValid = false;
        if (showPhoneInput) if (phoneNumber) {
            if (!isValidPhoneNumber(phoneNumber)) {
                setPhoneError('Please check the phone number you have entered');
                isValid = false;
            }
        } else {
            setPhoneError('Please enter a phone number');
            isValid = false;
        }
        if (showNameFields && isNameRequired) {
            if (!firstName) {
                setFirstNameError('Please enter your first name');
                isValid = false;
            }
            if (!lastName) {
                setLastNameError('Please enter your last name');
                isValid = false;
            }
        }
        if (termsText && !agreedToTerms) {
            setTermsError(true);
            isValid = false;
        }
        return isValid;
    }
    function handleSubmit(e) {
        e.preventDefault();
        if (!validateForm()) return;
        const formData = {
            ...showEmailInput && {
                email
            },
            ...showPhoneInput && {
                phoneNumber
            },
            ...showNameFields && {
                firstName,
                lastName
            },
            ...termsText && {
                agreedToTerms
            }
        };
        onSubmit(formData);
    }
    const qrCodeBlock = useAppDownloadLayout ? /*#__PURE__*/ jsx(QrCodeBlock, {
        qrCodeUrl: resolvedQrCodeUrl,
        qrCodeAlt: qrCodeAlt,
        qrCodeText: qrCodeText,
        qrCodeWrapperClass: styles.qrCodeWrapper(),
        qrCodeImageClass: styles.qrCodeImage(),
        qrCodeTextClass: styles.qrCodeText()
    }) : null;
    const formElement = /*#__PURE__*/ jsxs("form", {
        onSubmit: handleSubmit,
        className: styles.formContainer(),
        children: [
            termsText && /*#__PURE__*/ jsx("div", {
                className: styles.termsWrapper(),
                children: /*#__PURE__*/ jsx(CheckboxWithLabel, {
                    name: "terms",
                    value: "agreed",
                    checked: agreedToTerms,
                    handleChange: handleTermsChange,
                    size: "20",
                    emphasis: emphasis,
                    labelNode: /*#__PURE__*/ jsx(Span, {
                        className: termsError ? 'text-error-9' : void 0,
                        children: termsText
                    })
                })
            }),
            showNameFields && /*#__PURE__*/ jsxs("div", {
                className: styles.nameFieldsRow(),
                children: [
                    /*#__PURE__*/ jsx("div", {
                        className: styles.inputWrapper(),
                        children: /*#__PURE__*/ jsx(InputText, {
                            label: "First name",
                            isLabelHidden: true,
                            placeholder: firstNamePlaceholder,
                            value: firstName,
                            onChange: handleFirstNameChange,
                            errorMessage: firstNameError,
                            hasExternalError: Boolean(firstNameError),
                            emphasis: emphasis
                        })
                    }),
                    /*#__PURE__*/ jsx("div", {
                        className: styles.inputWrapper(),
                        children: /*#__PURE__*/ jsx(InputText, {
                            label: "Last name",
                            isLabelHidden: true,
                            placeholder: lastNamePlaceholder,
                            value: lastName,
                            onChange: handleLastNameChange,
                            errorMessage: lastNameError,
                            hasExternalError: Boolean(lastNameError),
                            emphasis: emphasis
                        })
                    })
                ]
            }),
            /*#__PURE__*/ jsxs("div", {
                className: styles.fieldsRow(),
                children: [
                    showEmailInput && /*#__PURE__*/ jsx("div", {
                        className: styles.inputWrapper(),
                        children: /*#__PURE__*/ jsx(InputText, {
                            label: "Email",
                            isLabelHidden: true,
                            placeholder: emailPlaceholder,
                            value: email,
                            onChange: handleEmailChange,
                            iconLeft: "mail",
                            errorMessage: emailError,
                            hasExternalError: Boolean(emailError),
                            emphasis: emphasis
                        })
                    }),
                    showPhoneInput && /*#__PURE__*/ jsx("div", {
                        className: styles.inputWrapper(),
                        children: /*#__PURE__*/ jsx(InputText, {
                            label: "Phone",
                            isLabelHidden: true,
                            placeholder: phonePlaceholder,
                            value: phoneNumber,
                            onChange: handlePhoneChange,
                            iconLeft: "call",
                            errorMessage: phoneError,
                            hasExternalError: Boolean(phoneError),
                            emphasis: emphasis
                        })
                    }),
                    /*#__PURE__*/ jsx("div", {
                        className: styles.buttonWrapper(),
                        children: /*#__PURE__*/ jsx(Button, {
                            type: "primary",
                            buttonType: "submit",
                            loading: isLoading,
                            disabled: isLoading,
                            fullWidth: useAppDownloadLayout ? false : !md,
                            className: useAppDownloadLayout ? 'w-full @xs:w-auto' : void 0,
                            size: "lg",
                            emphasis: emphasis,
                            children: buttonText
                        })
                    })
                ]
            })
        ]
    });
    const childrenSlotElement = childrenSlot ? /*#__PURE__*/ jsx("div", {
        className: styles.childrenSlot(),
        children: childrenSlot
    }) : null;
    const disclaimerElement = disclaimerText ? /*#__PURE__*/ jsx("div", {
        className: styles.disclaimerSlot(),
        children: disclaimerText
    }) : null;
    const layoutColumn = /*#__PURE__*/ jsxs("div", {
        className: styles.layoutColumn(),
        children: [
            childrenSlotElement,
            formElement,
            useAppDownloadLayout && disclaimerElement
        ]
    });
    const formBody = useAppDownloadLayout ? /*#__PURE__*/ jsxs("div", {
        className: styles.layoutWrapper(),
        children: [
            'left' === qrCodePosition && qrCodeBlock,
            layoutColumn,
            'right' === qrCodePosition && qrCodeBlock
        ]
    }) : /*#__PURE__*/ jsxs(Fragment, {
        children: [
            childrenSlotElement,
            formElement
        ]
    });
    if (successMessage) return /*#__PURE__*/ jsx("div", {
        className: cn(styles.root(), className),
        children: /*#__PURE__*/ jsx("div", {
            className: styles.bannerWrapper(),
            children: /*#__PURE__*/ jsx(StatusBanner, {
                palette: "success",
                emphasis: emphasis,
                captionNode: /*#__PURE__*/ jsx(Span, {
                    children: successMessage
                })
            })
        })
    });
    return /*#__PURE__*/ jsxs("div", {
        className: cn(styles.root(), '@container', className),
        children: [
            errorMessage && /*#__PURE__*/ jsx("div", {
                className: styles.bannerWrapper(),
                children: /*#__PURE__*/ jsx(StatusBanner, {
                    palette: "error",
                    emphasis: emphasis,
                    captionNode: /*#__PURE__*/ jsx(Span, {
                        children: errorMessage
                    })
                })
            }),
            formBody,
            !useAppDownloadLayout && disclaimerElement
        ]
    });
}
const AcquisitionForm_AcquisitionForm = /*#__PURE__*/ forwardRef(function(props, ref) {
    return /*#__PURE__*/ jsx(ResponsiveWrapper, {
        children: /*#__PURE__*/ jsx("div", {
            ref: ref,
            children: /*#__PURE__*/ jsx(AcquisitionFormContent, {
                ...props
            })
        })
    });
});
export { AcquisitionForm_AcquisitionForm as AcquisitionForm };
